<template>
  <div
    class="font-primary font-medium text-[17px] text-gray-800 pb-6 border-b border-[#DFDFDF]"
  >
    Subscriptions
  </div>

  <div class="border-b border-[#DFDFDF] pb-[27px] mt-5">
    <SettingsRow class="h-auto! border-none">
      <template #left>
        <Skeleton v-if="loading" class="w-[150px] h-[18px]" />

        <div v-else class="flex w-1/2">
          <DacrBadge
            class="min-w-[29px] max-w-[29px] h-[29px] mr-2.5"
            :icon-size="19"
          />

          <div class="mt-1">
            <div
              class="flex flex-col tablet-h:flex-row tablet-h:items-center mb-1.5"
            >
              <div
                class="font-semibold font-primary mb-1.5 tablet-h:mb-0 mr-2.5"
              >
                {{ billing?.plan.name }} plan
              </div>
              <Pill
                v-if="status"
                :warning="paymentRequiresAction"
                :danger="isPlanInactive"
              >
                {{ status }}
              </Pill>
            </div>
            <div class="font-primary text-[14px]">
              {{ billing?.plan.description }}
            </div>
            <div
              v-if="showPlanDetailsLink"
              class="text-green-700 font-medium mt-2 cursor-pointer w-fit"
              @click="handlePlanDetailsClick"
            >
              See plan details
            </div>
          </div>
        </div>
      </template>

      <template #right>
        <Skeleton v-if="loading" class="w-[150px] h-[18px]" />

        <div v-else class="w-1/2 mt-1 flex flex-col items-end">
          <div
            class="flex flex-col tablet-v:flex-row items-end font-primary text-[14px] font-medium"
          >
            <template v-if="prices.length">
              <div
                v-for="(price, index) in prices"
                :key="`price-${index}`"
                class="flex"
              >
                <div v-if="index" class="mr-1 tablet-v:ml-1">+</div>
                <div>
                  <template v-if="price.details.amount">
                    {{ formatPriceLocal(price) }}
                  </template>
                  {{ price.details.suffix }}
                </div>
              </div>
            </template>
            <div v-else>Free</div>
          </div>

          <template v-if="prices.length">
            <div
              class="font-primary text-[14px] leading-[22px] mb-3 text-right"
            >
              {{ billing?.plan.dateInfo?.prefix }}
              <span v-if="showBillingDate">
                {{ convertToFriendlyDate(billing?.plan.dateInfo?.date) }}
              </span>
            </div>

            <template v-if="isActiveSubscription">
              <template v-if="hasBundlePlan">
                <UiButton
                  v-if="user?.canUpgradePlan"
                  primary
                  class="mb-[17px]"
                  :disabled="paymentsDisabled"
                  @click="handleBundleUpgradeClick"
                >
                  {{ upgradeBundleCopy }}
                </UiButton>

                <div
                  v-if="user?.canChangePlan"
                  class="font-primary text-[14px] font-medium text-green-700 cursor-pointer mb-[17px]"
                  :class="disabledClasses"
                  @click="handleChangePlanClick"
                >
                  Change plan
                </div>
              </template>

              <template v-else>
                <UiButton
                  v-if="user?.canChangePlan"
                  primary
                  class="mb-[17px]"
                  :disabled="paymentsDisabled"
                  @click="handleChangePlanClick"
                >
                  Change plan
                </UiButton>
              </template>

              <div
                class="font-primary text-[14px] font-medium text-red-500 cursor-pointer"
                :class="disabledClasses"
                @click="handleCancelPlanClick"
              >
                Cancel plan
              </div>
            </template>

            <UiButton
              v-if="showPayButton"
              primary
              :disabled="paymentsDisabled"
              @click="handlePayClick"
            >
              Pay balance
            </UiButton>
          </template>

          <div
            v-else-if="user?.canUpgradePlan"
            class="mt-2.5 font-primary text-[14px] font-medium text-green-700 cursor-pointer"
            :class="disabledClasses"
            @click="handleUpgradeClick"
          >
            Upgrade to Dacr Pro
          </div>
        </div>
      </template>
    </SettingsRow>
  </div>

  <SettingsRow class="h-auto! py-[26px]">
    <template #left>
      <div>Payment method</div>
    </template>

    <template #right>
      <Skeleton v-if="loading" class="w-[100px] h-[18px]" />

      <div v-else class="whitespace-nowrap">
        <div v-if="isWebSubscription" class="flex flex-col items-end">
          <div
            v-if="billing?.paymentMethod?.card"
            class="flex items-center -mt-1 mb-1 font-primary text-[14px]"
          >
            <CreditCard
              :type="billing.paymentMethod.card.type"
              class="mr-2.5"
            />
            <div>•••• •••• •••• {{ billing?.paymentMethod?.card?.last4 }}</div>
            <div
              class="w-fit text-green-700 cursor-pointer ml-6 font-medium"
              :class="disabledClasses"
              @click="handleUpdatePaymentMethod"
            >
              Change
            </div>
          </div>

          <div
            v-else-if="!webviewStore.isActive"
            class="text-green-700 cursor-pointer font-medium"
            :class="disabledClasses"
            @click="handleUpdatePaymentMethod"
          >
            Add
          </div>

          <div v-else>- -</div>
        </div>

        <div v-else-if="platform && isPro" class="flex items-center">
          <Icon :name="platform as IconName" :size="18" class="mr-2" />
          {{ platform }} in-app purchase
        </div>

        <div v-else>- -</div>
      </div>
    </template>
  </SettingsRow>

  <SettingsRow class="h-auto! py-[26px] mb-[50px]">
    <template #left>
      <div class="flex flex-col w-2/3">
        <div class="mb-1.5 flex items-center">Next billings</div>
        <div class="font-secondary text-[13px] text-gray-800 opacity-50">
          Estimated charges are based on the number of your files currently on
          Dacr
        </div>
      </div>
    </template>

    <template #right>
      <Skeleton v-if="loading" class="w-[100px] h-[18px]" />

      <div v-else class="flex flex-col text-right">
        <template v-if="billing?.upcomingDetails.upcomingBilling">
          <div class="text-[14px] mb-5">
            <div class="font-secondary mb-[1px]">
              Due
              {{
                convertToFriendlyDate(
                  billing.upcomingDetails.upcomingBilling.date
                )
              }}
            </div>
            <div class="font-primary font-medium">
              {{ upcomingBillingTotal }}
            </div>
          </div>

          <div
            class="-mt-1.5 font-medium text-green-700 cursor-pointer"
            @click="handleUpcomingBillingDetailsClick"
          >
            See details
          </div>
        </template>

        <div v-else>- -</div>
      </div>
    </template>
  </SettingsRow>

  <div>
    <div class="font-primary font-medium text-[17px] text-gray-800 mb-1.5">
      Billing history
    </div>
    <div class="font-secondary text-[13px] text-gray-800 opacity-50 mb-[27px]">
      View invoices and receipts for all payments made on your Dacr account
    </div>

    <InvoicesTable
      :invoices="billing?.invoices.invoices"
      :hasMore="!!billing?.invoices.next"
      @fetchMore="handleFetchMoreInvoices"
    />
  </div>
</template>

<script setup lang="ts">
import {
  PlanStatus,
  PlanType,
  PlatformType,
  useBillingQueryQuery,
  UserPermission,
  type BillingPrice,
} from '@/shared/graphql/legacy/generated'
import { computed } from 'vue'
import SettingsRow from '@/features/settings/components/SettingsRow.vue'
import { formatPrice } from '@/shared/utils/money'
import { formatTimestamp } from '@/shared/utils/datetime'
import { UiButton, Icon, Pill, type IconName, Skeleton } from '@dacr/ui'
import CreditCard from '@/features/billing/components/CreditCard.vue'
import InvoicesTable from '@/features/billing/components/InvoicesTable.vue'
import { useWebviewStore } from '@/shared/stores/webview'
import { useUserStore } from '@/shared/stores/user'
import { useDevice } from '@/shared/composables/useDevice'
import { capitalize } from '@/shared/utils/string'
import { useBillingStore } from '@/features/billing/store'
import DacrBadge from '@/shared/components/DacrBadge.vue'
import { useRouter } from 'vue-router'
import { useDialog } from '@/shared/dialogs/useDialog'
import { Dialogs } from '@/shared/dialogs/types'

const { result, fetchMore, loading } = useBillingQueryQuery()

const device = useDevice()
const router = useRouter()
const dialog = useDialog()
const billingStore = useBillingStore()
const webviewStore = useWebviewStore()
const userStore = useUserStore()

const billing = computed(() => result.value?.billing)
const user = computed(() => userStore.user)

const convertToFriendlyDate = (date?: number) => {
  if (!date) return ''
  return formatTimestamp(date, { formatString: 'LLLL d, yyyy' })
}

const isPro = computed(() =>
  user.value?.permissions.includes(UserPermission.Pro)
)

const isActiveSubscription = computed(
  () => billing.value?.plan.status === PlanStatus.Active
)

const isWebSubscription = computed(
  () => billing.value?.plan.platform === PlatformType.Web
)

const platform = computed(() => capitalize(billing.value?.plan.platform || ''))

const paymentsDisabled = computed(
  () => !user.value?.permissions.includes(UserPermission.Payments)
)

const disabledClasses = computed(() => ({
  'opacity-50 pointer-events-none': paymentsDisabled.value,
}))

const isPlanInactive = computed(
  () =>
    billing.value?.plan.status === PlanStatus.Overdue ||
    billing.value?.plan.status === PlanStatus.Canceled
)

const paymentRequiresAction = computed(
  () => billing.value?.plan.status === PlanStatus.RequiresAction
)

const showPayButton = computed(
  () => billing.value?.plan.status === PlanStatus.Overdue
)

const hasBundlePlan = computed(
  () => billing.value?.plan.type === PlanType.Bundle
)

const showPlanDetailsLink = computed(() =>
  userStore.user?.permissions.includes(UserPermission.Pro)
)

const upgradeBundleCopy = computed(() => {
  if (device.viewportWidth.value >= 428) {
    return 'Increase bundle limit'
  }
  return 'Increase limit'
})

const formatPriceLocal = (price: BillingPrice) => {
  return formatPrice({
    amount: (price.details.amount || 0) / 100,
    currency: price.details.currency,
  })
}

const upcomingBillingTotal = computed(() => {
  return formatPrice({
    amount: (user.value?.upcomingBilling?.total || 0) / 100,
    currency: user.value?.upcomingBilling?.currency,
  })
})

const prices = computed(() => {
  const allPrices = billing.value?.plan?.prices || []
  return allPrices.filter((price) => !!price.details.amount)
})

const status = computed(() => {
  if (billing.value?.plan.status === PlanStatus.Canceled) return 'Cancelled'
  if (billing.value?.plan.status === PlanStatus.Overdue) return 'Overdue'
  if (billing.value?.plan.status === PlanStatus.RequiresAction)
    return 'Requires action'
  return ''
})

const showBillingDate = computed(() => {
  return (
    billing.value?.plan.dateInfo?.date &&
    billing.value.plan.status !== PlanStatus.Overdue
  )
})

const handlePayClick = () => {
  if (!billingStore.canUpdateSubscription && webviewStore.isActive) {
    return dialog.open(Dialogs.CantPay)
  }

  dialog.open(Dialogs.Pay, {
    events: {
      success() {
        dialog.open(Dialogs.Success, {
          props: { title: 'Payment successful' },
          events: {
            success() {
              dialog.close()
            },
          },
        })
      },
    },
  })
}

const handleChangePlanClick = () => {
  if (!billingStore.canUpdateSubscription) {
    return dialog.open(Dialogs.IAP, {
      props: { type: 'change' },
    })
  }
  router.push('/change-plans')
}

const handleUpgradeClick = () => {
  if (!billingStore.canUpdateSubscription) {
    return dialog.open(Dialogs.IAP, {
      props: { type: 'change' },
    })
  }
  router.push('/upgrade')
}

const handlePlanDetailsClick = () => {
  dialog.open(Dialogs.PlanDetails, {
    props: { prices: billing.value?.plan.details.prices },
  })
}

const handleUpcomingBillingDetailsClick = () => {
  dialog.open(Dialogs.UpcomingBillingDetails, {
    props: { details: billing.value?.upcomingDetails },
  })
}

const handleUpdatePaymentMethod = () => {
  if (!billingStore.canUpdateSubscription) {
    return dialog.open(Dialogs.IAP, {
      props: { type: 'change' },
    })
  }
  dialog.open(Dialogs.UpdatePaymentMethod)
}

const handleCancelPlanClick = () => {
  if (!billingStore.canUpdateSubscription && !webviewStore.isActive) {
    return dialog.open(Dialogs.IAP, { props: { type: 'cancel' } })
  }
  dialog.open(Dialogs.CancelSubscription)
}

const handleFetchMoreInvoices = () => {
  fetchMore({
    variables: { nextInvoice: billing.value?.invoices.next },
    updateQuery: (previousData, { fetchMoreResult }) => {
      if (!fetchMoreResult) return previousData

      return {
        ...previousData,
        billing: {
          ...previousData.billing,
          invoices: {
            __typename: 'InvoicesResponse' as const,
            invoices: previousData.billing.invoices.invoices.concat(
              fetchMoreResult.billing.invoices.invoices
            ),
            next: fetchMoreResult.billing.invoices.next,
          },
        },
      }
    },
  })
}

const handleBundleUpgradeClick = () => {
  if (!billingStore.canUpdateSubscription) {
    return dialog.open(Dialogs.IAP, {
      props: { type: 'change' },
    })
  }
  dialog.open(Dialogs.UpgradeBundleLimit)
}
</script>
